import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Chip,
  Divider,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  TextField,
} from "@mui/material";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "./firebase";
import { getDisplayName } from "./utils/statusLabels";
import { getMaxQtyForItem } from "./utils/materialRequestConstants";
import ConfirmDialog from "./components/ConfirmDialog";
import { apiFetch, API_BASE } from "./utils/apiClient";
import { toDateSafe } from "./utils/toDateSafe";
import UploadPreview from "./components/UploadPreview";

const ROLE_LABEL = {
  workshop: "🧰 الورشة",
  illustrator: "🎨 الرسام",
};

const formatDate = (ts) => {
  const d = toDateSafe(ts);
  if (!d) return "-";
  return `${d.toLocaleDateString("en-GB")} ${d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

export default function MaterialRequestsAdmin() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState("الكل");
  const [uploadingId, setUploadingId] = useState(null);
  const [justFulfilledId, setJustFulfilledId] = useState(null);

  const currentUser = localStorage.getItem("username") || "مدير";
  const isAdmin = currentUser === "admin";
  const isBaneen = currentUser === "Baneen@123";

  const [adjustTarget, setAdjustTarget] = useState(null); // { requestId, itemIndex, item, role }
  const [adjustQty, setAdjustQty] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [adjustSaving, setAdjustSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchRequests = async () => {
    setLoading(true);

    const res = await apiFetch(`${API_BASE}/material-requests`);
    const data = await res.json();

    if (!res.ok) {
      console.error("Error fetching material requests:", data.error);
      setLoading(false);
      return;
    }

    data.sort((a, b) => (toDateSafe(b.createdAt)?.getTime() || 0) - (toDateSafe(a.createdAt)?.getTime() || 0));
    setRequests(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const filtered = useMemo(() => {
    if (roleFilter === "الكل") return requests;
    return requests.filter((r) => r.role === roleFilter);
  }, [requests, roleFilter]);

  const pending = filtered.filter((r) => r.status !== "fulfilled");
  const history = filtered.filter((r) => r.status === "fulfilled");

  const markFulfilled = async (id) => {
    await apiFetch(`${API_BASE}/material-requests/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        status: "fulfilled",
        fulfilledAt: true,
        fulfilledBy: currentUser,
      }),
    });

    setRequests((prev) =>
      prev.map((r) =>
        r.id === id
          ? {
              ...r,
              status: "fulfilled",
              fulfilledAt: new Date(),
              fulfilledBy: currentUser,
            }
          : r
      )
    );
    setJustFulfilledId(id);
  };

  const handleAttachFiles = async (id, fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const existing = requests.find((r) => r.id === id)?.attachmentUrls || [];
    await saveAttachments(id, [...existing, ...files]);
  };

  // يحفظ قائمة المرفقات (روابط موجودة + ملفات جديدة تنرفع بمكانها) - يُستخدم
  // للإضافة وللحذف/التبديل من المعاينة
  const saveAttachments = async (id, list) => {
    const existing = requests.find((r) => r.id === id)?.attachmentUrls || [];
    if (list.length < existing.length && !window.confirm("متأكد تريد تحذف هذا المرفق؟")) {
      return;
    }

    setUploadingId(id);
    try {
      const newUrls = [];
      for (const item of list) {
        if (typeof item === "string") {
          newUrls.push(item);
          continue;
        }
        const fileRef = ref(storage, `materialRequests/${id}/${Date.now()}-${item.name}`);
        await uploadBytes(fileRef, item);
        newUrls.push(await getDownloadURL(fileRef));
      }

      await apiFetch(`${API_BASE}/material-requests/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attachmentUrls: newUrls }),
      });

      setRequests((prev) =>
        prev.map((r) => (r.id === id ? { ...r, attachmentUrls: newUrls } : r))
      );
    } catch (err) {
      console.error("Error uploading material request attachment:", err);
      window.alert("حدث خطأ أثناء رفع الملف");
    } finally {
      setUploadingId(null);
    }
  };

  const openAdjust = (requestId, itemIndex, item) => {
    setAdjustTarget({ requestId, itemIndex, item });
    setAdjustQty(String(item.quantity));
    setAdjustReason("");
  };

  const saveAdjust = async () => {
    if (!adjustTarget || !adjustReason.trim()) return;

    const { requestId, itemIndex, item } = adjustTarget;
    const cap = getMaxQtyForItem(item.label);
    const newQuantity = Math.min(Math.max(Number(adjustQty) || 0, 0), cap);

    const target = requests.find((r) => r.id === requestId);
    if (!target) return;

    const updatedItems = target.items.map((it, idx) =>
      idx === itemIndex ? { ...it, quantity: newQuantity } : it
    );
    const newAdjustment = {
      itemLabel: item.label,
      oldQuantity: item.quantity,
      newQuantity,
      reason: adjustReason.trim(),
      changedBy: currentUser,
      changedAt: new Date(), // السيرفر يحسب القيمة الفعلية المحفوظة
    };
    const updatedAdjustments = [...(target.adjustments || []), newAdjustment];

    setAdjustSaving(true);
    try {
      const res = await apiFetch(`${API_BASE}/material-requests/${requestId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: updatedItems,
          adjustments: updatedAdjustments,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || "فشل حفظ التعديل");
      }

      setRequests((prev) =>
        prev.map((r) =>
          r.id === requestId
            ? { ...r, items: updatedItems, adjustments: updatedAdjustments }
            : r
        )
      );
      setAdjustTarget(null);
      setAdjustQty("");
      setAdjustReason("");
    } catch (err) {
      console.error("Error adjusting material request quantity:", err);
      window.alert("حدث خطأ أثناء حفظ التعديل");
    } finally {
      setAdjustSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    try {
      const res = await apiFetch(`${API_BASE}/material-requests/${deleteTarget.id}`, {
        method: "DELETE",
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || "فشل حذف الطلب");
      }

      setRequests((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      console.error("Error deleting material request:", err);
      window.alert("حدث خطأ أثناء حذف الطلب");
    } finally {
      setDeleting(false);
    }
  };

  const renderItems = (r) => (
    <Box mt={1}>
      {r.items?.length > 0 && (
        <Box display="flex" flexDirection="column" gap={0.3}>
          {r.items.map((item, idx) => (
            <Box
              key={idx}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
            >
              <Typography fontSize={14}>
                • {item.label}
                {item.size ? ` — القياس: ${item.size}` : ""} — العدد:{" "}
                <strong>{item.quantity}</strong>
              </Typography>
              {(isAdmin || isBaneen) && (
                <Button
                  size="small"
                  onClick={() => openAdjust(r.id, idx, item)}
                  sx={{ minWidth: 0, px: 1 }}
                >
                  ✏️
                </Button>
              )}
            </Box>
          ))}
        </Box>
      )}
      {r.notes && (
        <Typography fontSize={14} sx={{ mt: 0.5 }}>
          📝 {r.notes}
        </Typography>
      )}
      {r.adjustments?.length > 0 && (
        <Box mt={1}>
          <Typography fontSize={12.5} fontWeight={800} color="text.secondary">
            🔄 سجل التعديلات:
          </Typography>
          {r.adjustments.map((a, idx) => (
            <Typography key={idx} fontSize={12.5} color="text.secondary">
              • {a.itemLabel}: {a.oldQuantity} → {a.newQuantity} — {a.reason} (بواسطة{" "}
              {getDisplayName(a.changedBy) || a.changedBy}، {formatDate(a.changedAt)})
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );

  const renderAttachments = (r) =>
    r.attachmentUrls?.length > 0 && (
      <Box mt={1}>
        <UploadPreview
          files={r.attachmentUrls}
          disabled={uploadingId === r.id}
          onChange={(list) => saveAttachments(r.id, list)}
        />
      </Box>
    );

  const renderUploadControl = (r) => (
    <Box mt={1.5}>
      <Button
        component="label"
        variant="outlined"
        size="small"
        disabled={uploadingId === r.id}
        sx={{ fontWeight: 700 }}
      >
        {uploadingId === r.id
          ? "جارٍ الرفع..."
          : "📎 رفع فاتورة أو صورة (اختياري)"}
        <input
          type="file"
          hidden
          multiple
          accept="image/*,application/pdf"
          onChange={(e) => handleAttachFiles(r.id, e.target.files)}
        />
      </Button>
    </Box>
  );

  return (
    <Container dir="rtl" sx={{ mt: 3, mb: 4 }}>
      <Box sx={{ maxWidth: 1100, mx: "auto" }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
          mb={2}
        >
          <Typography variant="h4" fontWeight={900}>
            📦 طلبات المواد
          </Typography>

          <Box display="flex" gap={1} flexWrap="wrap">
            <Chip
              label={`قيد الانتظار: ${pending.length}`}
              color="warning"
              sx={{ fontWeight: 800 }}
            />
            <Chip
              label={`تم الطلب: ${history.length}`}
              color="success"
              sx={{ fontWeight: 800 }}
            />
          </Box>
        </Box>

        <Paper sx={{ p: 2.5, borderRadius: 3, boxShadow: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ right: 0, left: "auto" }}>القسم</InputLabel>
                <Select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                  <MenuItem value="الكل">الكل</MenuItem>
                  <MenuItem value="workshop">🧰 الورشة</MenuItem>
                  <MenuItem value="illustrator">🎨 الرسام</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Paper sx={{ p: 2.5, borderRadius: 3, boxShadow: 2, mb: 3 }}>
              <Typography fontWeight={900} mb={2}>
                🕓 قيد الانتظار
              </Typography>

              {pending.length === 0 ? (
                <Alert severity="info">لا توجد طلبات مواد قيد الانتظار</Alert>
              ) : (
                <Box display="flex" flexDirection="column" gap={2}>
                  {pending.map((r) => (
                    <Paper key={r.id} sx={{ p: 2, borderRadius: 3, border: "1px solid #eee" }}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        flexWrap="wrap"
                        gap={1}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            size="small"
                            label={ROLE_LABEL[r.role] || r.role}
                            color="primary"
                            sx={{ fontWeight: 800 }}
                          />
                          <Typography fontWeight={900}>
                            {getDisplayName(r.requestedBy)} — {formatDate(r.createdAt)}
                          </Typography>
                        </Box>

                        <Box display="flex" gap={1}>
                          <Button
                            variant="contained"
                            color="success"
                            onClick={() => markFulfilled(r.id)}
                            sx={{ fontWeight: 900 }}
                          >
                            ✅ تم الطلب
                          </Button>
                          {isAdmin && (
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={() => setDeleteTarget(r)}
                              sx={{ fontWeight: 800 }}
                            >
                              🗑️ حذف الطلب
                            </Button>
                          )}
                        </Box>
                      </Box>

                      {renderItems(r)}

                      {justFulfilledId === r.id && (
                        <>
                          <Divider sx={{ my: 1.5 }} />
                          {renderUploadControl(r)}
                          {renderAttachments(r)}
                        </>
                      )}
                    </Paper>
                  ))}
                </Box>
              )}
            </Paper>

            <Paper sx={{ p: 2.5, borderRadius: 3, boxShadow: 2 }}>
              <Typography fontWeight={900} mb={2}>
                📜 هستوري الطلبات
              </Typography>

              {history.length === 0 ? (
                <Alert severity="warning">لا يوجد سجل بعد</Alert>
              ) : (
                <Box display="flex" flexDirection="column" gap={2}>
                  {history.map((r) => (
                    <Paper key={r.id} sx={{ p: 2, borderRadius: 3, border: "1px solid #eee" }}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        flexWrap="wrap"
                        gap={1}
                      >
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Chip
                            size="small"
                            label={ROLE_LABEL[r.role] || r.role}
                            color="primary"
                            sx={{ fontWeight: 800 }}
                          />
                          <Chip size="small" label="تم الطلب" color="success" sx={{ fontWeight: 800 }} />
                          <Typography fontWeight={900}>{getDisplayName(r.requestedBy)}</Typography>
                        </Box>

                        {isAdmin && (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => setDeleteTarget(r)}
                            sx={{ fontWeight: 800 }}
                          >
                            🗑️ حذف الطلب
                          </Button>
                        )}
                      </Box>

                      <Box mt={1}>
                        <Typography fontSize={13} color="text.secondary">
                          📅 تاريخ الطلب: {formatDate(r.createdAt)}
                        </Typography>
                        <Typography fontSize={13} color="text.secondary">
                          ✅ تاريخ التنفيذ: {formatDate(r.fulfilledAt)}
                          {r.fulfilledBy ? ` — بواسطة ${r.fulfilledBy}` : ""}
                        </Typography>
                      </Box>

                      {renderItems(r)}
                      {renderAttachments(r)}
                      {renderUploadControl(r)}
                    </Paper>
                  ))}
                </Box>
              )}
            </Paper>
          </>
        )}
      </Box>

      <ConfirmDialog
        open={!!adjustTarget}
        title={adjustTarget ? `تعديل كمية: ${adjustTarget.item.label}` : ""}
        description="عدّل الكمية إذا تغيّر المتوفر من المورد، ولازم تكتب سبب التعديل حتى يوصل للطرفين."
        confirmLabel="حفظ التعديل"
        cancelLabel="إلغاء"
        loading={adjustSaving}
        disabled={!adjustReason.trim()}
        onConfirm={saveAdjust}
        onCancel={() => setAdjustTarget(null)}
      >
        <Box display="flex" flexDirection="column" gap={2} mb={1}>
          <TextField
            label="الكمية الجديدة"
            type="number"
            size="small"
            value={adjustQty}
            inputProps={{
              min: 0,
              max: adjustTarget ? getMaxQtyForItem(adjustTarget.item.label) : undefined,
            }}
            onChange={(e) => setAdjustQty(e.target.value)}
          />
          <TextField
            label="سبب التعديل *"
            placeholder="مثال: المورد ما عنده إلا نص الكمية المطلوبة حالياً"
            multiline
            minRows={2}
            value={adjustReason}
            onChange={(e) => setAdjustReason(e.target.value)}
            error={!adjustReason.trim()}
            helperText={!adjustReason.trim() ? "السبب إجباري" : ""}
          />
        </Box>
      </ConfirmDialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="حذف الطلب نهائياً"
        description="هذا الإجراء ما يمكن التراجع عنه — الطلب وكل تفاصيله (المواد، المرفقات، سجل التعديلات) بينحذف نهائياً. تأكد قبل المتابعة."
        confirmLabel="حذف نهائياً"
        cancelLabel="تراجع"
        destructive
        loading={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Container>
  );
}
